<template>
    <!-- Modal Overlay -->
    <div
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
        v-if="showProgramUserOptionsModal"
        aria-labelledby="program-options-modal"
        role="dialog"
        aria-modal="true"
        @click.self="closeModal()"
    >
        <!-- Modal Container -->
        <div
            class="flex w-[600px] p-6 flex-col items-start rounded-lg bg-white dark:bg-slate-800 shadow-lg shadow-black/10 dark:shadow-slate-900/50"
            @click.stop
        >
            <!-- Header -->
            <div class="flex items-center justify-between self-stretch pb-8">
                <!-- Title -->
                <h2 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7">
                    Settings
                </h2>
                <!-- Close Button -->
                <button
                    class="flex p-2 items-center justify-center rounded border-0 bg-transparent hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
                    @click="closeModal()"
                    type="button"
                    aria-label="Close modal"
                >
                    <Icon
                        icon="close"
                        :width="20"
                        :height="20"
                        class="text-gray-500 dark:text-gray-400"
                    />
                </button>
            </div>

            <!-- Content -->
            <div class="flex flex-col items-start self-stretch">
                <!-- Program Section -->
                <div
                    class="flex flex-col justify-center items-start self-stretch pb-2"
                >
                    <!-- Program Label -->
                    <div class="flex pb-2 items-start gap-2.5 self-stretch">
                        <span
                            class="flex-1 text-slate-600 dark:text-slate-300 dark:text-slate-400 text-sm font-medium leading-5"
                        >
                            Program
                        </span>
                    </div>
                    <!-- Program Info -->
                    <div class="flex items-center gap-3 self-stretch">
                        <!-- Program Image -->
                        <div
                            class="flex w-28 h-12 p-1.5 px-3.5 justify-center items-center rounded-lg border border-gray-200 dark:border-slate-700"
                        >
                            <img
                                v-if="reward.image || reward.program_image"
                                :src="getProgramImageUrl(reward.image || reward.program_image)"
                                :alt="reward.name"
                                class="max-w-full max-h-full object-contain"
                            />
                        </div>
                        <!-- Program Name -->
                        <div class="flex flex-col flex-1">
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ reward.name }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Connection Status -->
                <div
                    v-if="reward.connected && reward.bank"
                    class="flex items-center gap-2 self-stretch"
                >
                    <Icon
                        icon="bank"
                        :width="16"
                        :height="16"
                        class="text-gray-400"
                    />
                    <span class="text-slate-500 dark:text-slate-400 text-xs font-normal leading-4">
                        This program is connected to your
                        {{ reward.bank.name }} account
                    </span>
                </div>

                <!-- Points Update Settings Section -->
                <div
                    class="flex flex-col justify-center items-start self-stretch pt-8"
                    ref="radioSection"
                >
                    <!-- Settings Label -->
                    <div class="flex pb-4 items-start gap-2.5 self-stretch">
                        <span
                            class="flex-1 text-slate-600 dark:text-slate-300 dark:text-slate-400 text-sm font-medium leading-5"
                        >
                            Settings
                        </span>
                    </div>

                    <!-- Radio Options -->
                    <div class="flex flex-col gap-4 self-stretch">
                        <!-- Connect through bank option -->
                        <div
                            v-if="
                                reward.connected ||
                                (reward.institutions &&
                                    reward.institutions[reward.id] &&
                                    reward.institutions[reward.id].length > 0)
                            "
                            class="flex p-4 flex-col items-start gap-3 self-stretch rounded-lg border-2 cursor-pointer"
                            :class="
                                isBankOptionSelected
                                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                            "
                            @click="
                                if (!reward.program_user) {
                                    reward.program_user = {
                                        program_id: reward.id,
                                        rewards_source: 'mx',
                                    };
                                } else {
                                    reward.program_user.rewards_source = 'mx';
                                }
                                setRewardsOrigin();
                                $event.stopPropagation();
                            "
                        >
                            <!-- Radio Header -->
                            <div
                                class="flex items-start justify-between self-stretch"
                            >
                                <div class="flex flex-col items-start gap-2">
                                    <Icon
                                        icon="bank"
                                        :width="20"
                                        :height="20"
                                        class="text-gray-600 dark:text-gray-300"
                                    />
                                    <div
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                    >
                                        Connect through bank
                                    </div>
                                </div>
                                <div
                                    class="w-4 h-4 rounded-full flex items-center justify-center border-2"
                                    :class="
                                        isBankOptionSelected
                                            ? 'bg-red-500 border-red-500'
                                            : 'bg-white dark:bg-slate-800 border-gray-300'
                                    "
                                >
                                    <div
                                        v-if="isBankOptionSelected"
                                        class="w-2 h-2 rounded-full bg-white dark:bg-slate-800"
                                    ></div>
                                </div>
                            </div>
                            <!-- Radio Benefits -->
                            <div
                                class="flex flex-col items-start gap-1 self-stretch"
                            >
                                <div
                                    class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                >
                                    &bull; Regular rewards syncing
                                </div>
                                <div
                                    class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                >
                                    &bull; Available at no cost
                                </div>
                            </div>
                        </div>

                        <!-- Manual updates option -->
                        <div
                            class="flex p-4 flex-col items-start gap-3 self-stretch rounded-lg border-2 cursor-pointer"
                            :class="
                                isManualOptionSelected
                                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                            "
                            @click="
                                if (!reward.program_user) {
                                    reward.program_user = {
                                        program_id: reward.id,
                                        rewards_source: 'manual',
                                    };
                                } else {
                                    reward.program_user.rewards_source =
                                        'manual';
                                }
                                setRewardsOrigin();
                                $event.stopPropagation();
                            "
                        >
                            <!-- Radio Header -->
                            <div
                                class="flex items-start justify-between self-stretch"
                            >
                                <div class="flex flex-col items-start gap-2">
                                    <Icon
                                        icon="edit"
                                        :width="20"
                                        :height="20"
                                        class="text-gray-600 dark:text-gray-300"
                                    />
                                    <div
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                    >
                                        Manual updates
                                    </div>
                                </div>
                                <div
                                    class="w-4 h-4 rounded-full flex items-center justify-center border-2"
                                    :class="
                                        isManualOptionSelected
                                            ? 'bg-red-500 border-red-500'
                                            : 'bg-white dark:bg-slate-800 border-gray-300'
                                    "
                                >
                                    <div
                                        v-if="isManualOptionSelected"
                                        class="w-2 h-2 rounded-full bg-white dark:bg-slate-800"
                                    ></div>
                                </div>
                            </div>
                            <!-- Radio Description -->
                            <div
                                class="flex flex-col items-start gap-1 self-stretch"
                            >
                                <div
                                    class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                >
                                    Manually update your points
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Error Messages -->
                <div>
                    <Transition name="fade">
                        <Errors :errors="errors" />
                    </Transition>
                </div>
            </div>

            <!-- Footer Buttons -->
            <div class="flex justify-end items-start gap-2 self-stretch pt-8">
                <!-- Cancel Button -->
                <button
                    type="button"
                    class="flex py-2.5 px-4 items-start gap-2 rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    @click="closeModal()"
                >
                    Cancel
                </button>
                <!-- Save Button -->
                <button
                    type="button"
                    class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-700 shadow-sm text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                    @click="saveProgramOptions"
                    :disabled="saveProgramBtnAction"
                >
                    <span v-if="saveProgramBtnAction" class="animate-spin">
                        ⟳
                    </span>
                    <span v-else>Save</span>
                </button>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { usePointsStore } from '@/store/points';
import { notify } from 'notiwind';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    components: {
        ActionButton,
        Errors,
        Icon,
    },
    data: () => ({
        errors: [],
    }),
    watch: {},
    computed: {
        ...mapState(usePointsStore, [
            'showProgramUserOptionsModal',
            'reward',
            'saveProgramBtnAction',
        ]),
        cdnUrl() {
            return window.cdnUrl;
        },
        debugInfo() {
            return null;
        },
        isBankOptionSelected() {
            if (!this.reward || !this.reward.program_user) {
                return false;
            }

            const currentSource = this.reward.program_user.rewards_source;

            if (
                this.reward.institutions &&
                this.reward.institutions[this.reward.id] &&
                this.reward.institutions[this.reward.id].length > 0
            ) {
                const bank = this.reward.institutions[this.reward.id][0];
                const bankCode = bank.institution_code || 'mx';
                return (
                    currentSource === bankCode ||
                    currentSource === 'mx' ||
                    (this.reward.connected && !currentSource)
                );
            }

            if (this.reward.connected) {
                return (
                    currentSource !== 'manual' ||
                    (this.reward.connected && !currentSource)
                );
            }

            return false;
        },
        isManualOptionSelected() {
            if (!this.reward || !this.reward.program_user) {
                return false;
            }

            return this.reward.program_user.rewards_source === 'manual';
        },
    },
    methods: {
        ...mapActions(usePointsStore, [
            'setShowProgramModal',
            'setRewardsOrigin',
            'editProgram',
        ]),
        getProgramImageUrl(image) {
            if (!image) return '';
            // Handle object format (program_image relation)
            if (typeof image === 'object' && image.path && image.file_name) {
                return `${this.cdnUrl}/${image.path}/_small/${image.file_name}`;
            }
            // Handle both formats: program_images/ and programs_images/
            const cleanImage = image.replace(/^programs?_images\//, '');
            return `https://my10x.nyc3.cdn.digitaloceanspaces.com/uploads/program_images/_small/${cleanImage}`;
        },
        saveProgramOptions() {
            this.setRewardsOrigin();

            // Show success notification
            notify({
                group: 'success',
                title: 'Points update settings changed',
                text: 'Update settings were successfully saved.',
            });

            this.setShowProgramModal(false);
            if (
                this.reward &&
                this.reward.program_user &&
                this.reward.program_user.rewards_source === 'manual'
            ) {
                setTimeout(() => {
                    this.editProgram(
                        this.reward,
                        this.reward.program_user || this.reward.user
                    );
                }, 300);
            }
        },
        closeModal() {
            this.setShowProgramModal(false);
        },
    },
    created() {},
    mounted() {},
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
